import { createSlice } from "@reduxjs/toolkit";
import { ErrorType, RootState } from "../../../app/store";
import {
  BankAccountDTO,
  BankAccountSearchResponse
} from "@inosio/payments-sdk";

interface BankAccountsDataPage {
  Rows: Array<BankAccountDTO>;
}

type BankAccountState = {
  BankAccounts: BankAccountSearchResponse;
  BankAccountsDataPages: Array<BankAccountsDataPage>;
  isFetchingBankAccounts: Boolean;
  isFetchingBankAccountsFailed: Boolean;
  isFetchingBankAccountsSuccess: Boolean;
  errorMessage: ErrorType;
  isCreatingBankAccount: Boolean;
  isCreatingBankAccountSuccess: Boolean;
  isCreatingBankAccountFailed: Boolean;
  createdBankAccount: BankAccountDTO,
  isUpdatingBankAccount: Boolean;
  isUpdatingBankAccountSuccess: Boolean;
  isUpdatingBankAccountFailed: Boolean;
  isDeletingBankAccount: Boolean;
  isDeletingBankAccountSuccess: Boolean;
  isDeletingBankAccountFailed: Boolean;
};

const getInitialState = () => {
  return {
    BankAccounts: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    BankAccountsDataPages: [],
    isFetchingBankAccounts: false,
    isFetchingBankAccountsFailed: false,
    isFetchingBankAccountsSuccess: false,
    errorMessage: {} as ErrorType,
    isCreatingBankAccount: false,
    isCreatingBankAccountSuccess: false,
    isCreatingBankAccountFailed: false,
    createdBankAccount: {} as BankAccountDTO,
    isUpdatingBankAccount: false,
    isUpdatingBankAccountSuccess: false,
    isUpdatingBankAccountFailed: false,
    isDeletingBankAccount: false,
    isDeletingBankAccountSuccess: false,
    isDeletingBankAccountFailed: false,
  } as BankAccountState;
};

const bankAccountsSlice = createSlice({
  name: "bankAccounts",
  initialState: getInitialState(),
  reducers: {
    getFetchingBankAccountsData(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isFetchingBankAccountsFailed = false;
      state.isFetchingBankAccounts = true;
    },
    setFetchingBankAccountsData(state, action) {
      const _payload = action.payload;
      let currentBankAccountPages = [...state.BankAccountsDataPages];
      if (_payload.RecordStart === 0)
        currentBankAccountPages = [];

      const whatPageResultsIsThis = Math.ceil(
        _payload.RecordStart / _payload.RangeSize
      );
      currentBankAccountPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows,
      };
      const baData = {
        BankAccounts: action.payload,
        BankAccountsDataPages: currentBankAccountPages,
        isFetchingBankAccounts: false,
        isFetchingBankAccountsFailed: false,
        isFetchingBankAccountsSuccess: true,
      };
      return { ...state, ...baData };
    },
    getFetchingBankAccountsDataFailed(state, action) {
      state.isFetchingBankAccountsFailed = true;
      state.isFetchingBankAccounts = false;
      state.errorMessage = action.payload;
      state.isFetchingBankAccountsSuccess = false;
    },
    getCreatingBankAccount(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isCreatingBankAccountFailed = false;
      state.isCreatingBankAccountSuccess = false;
      state.isCreatingBankAccount = true;
    },
    setCreatingBankAccount(state, action) {
      state.createdBankAccount = action.payload as BankAccountDTO;
      state.isCreatingBankAccountFailed = false;
      state.isCreatingBankAccountSuccess = true;
      state.isCreatingBankAccount = false;
    },
    getCreatingBankAccountFailed(state, action) {
      state.isCreatingBankAccountFailed = true;
      state.isCreatingBankAccountSuccess = false;
      state.isCreatingBankAccount = false;
      state.errorMessage = action.payload;
    },
    getUpdatingBankAccount(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isUpdatingBankAccountSuccess = false;
      state.isUpdatingBankAccountFailed = false;
      state.isUpdatingBankAccount = true;
    },
    setUpdatingBankAccount(state, action) {
      state.isUpdatingBankAccountSuccess = true;
      state.isUpdatingBankAccountFailed = false;
      state.isUpdatingBankAccount = false;
    },
    getUpdatingBankAccountFailed(state, action) {
      state.isUpdatingBankAccountSuccess = false;
      state.isUpdatingBankAccountFailed = true;
      state.isUpdatingBankAccount = false;
      state.errorMessage = action.payload;
    },
    getDeletingBankAccount(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isDeletingBankAccountSuccess = false;
      state.isDeletingBankAccountFailed = false;
      state.isDeletingBankAccount = true;
    },
    setDeletingBankAccount(state, action) {
      state.isDeletingBankAccountSuccess = true;
      state.isDeletingBankAccountFailed = false;
      state.isDeletingBankAccount = false;
    },
    getDeletingBankAccountFailed(state, action) {
      state.isDeletingBankAccountSuccess = false;
      state.isDeletingBankAccountFailed = true;
      state.isDeletingBankAccount = false;
      state.errorMessage = action.payload;
    },
    getResetBankAccountsState(state) {
      return {
        ...state,
        isFetchingBankAccounts: false,
        isFetchingBankAccountsFailed: false,
        isFetchingBankAccountsSuccess: false,
        isCreatingBankAccount: false,
        isCreatingBankAccountSuccess: false,
        isCreatingBankAccountFailed: false,
        createdBankAccount: {} as BankAccountDTO,
        isUpdatingBankAccount: false,
        isUpdatingBankAccountSuccess: false,
        isUpdatingBankAccountFailed: false,
        isDeletingBankAccount: false,
        isDeletingBankAccountSuccess: false,
        isDeletingBankAccountFailed: false,
        errorMessage: {} as ErrorType,
      }
    },
  },
});

export const {
  getFetchingBankAccountsData,
  setFetchingBankAccountsData,
  getFetchingBankAccountsDataFailed,
  getCreatingBankAccount,
  setCreatingBankAccount,
  getCreatingBankAccountFailed,
  getUpdatingBankAccount,
  setUpdatingBankAccount,
  getUpdatingBankAccountFailed,
  getDeletingBankAccount,
  setDeletingBankAccount,
  getDeletingBankAccountFailed,
  getResetBankAccountsState,
} = bankAccountsSlice.actions

export default bankAccountsSlice.reducer;
export const selectBankAccounts = (state: RootState) => state.bankAccountModuleState.BankAccounts;
export const selectBankAccountsCurrentPage = (state: RootState) =>
  state.bankAccountModuleState.BankAccountsDataPages;
export const selectBankAccountsState = (state: RootState) => state.bankAccountModuleState;
